import { useState } from 'react';
import { Modal, Pill } from '../../components/ui/common';
import { Icon } from '../../components/ui/icon';
import { toast } from '../../components/ui/toast';
import { errorMessage } from '../../lib/api';
import { useCan } from '../../lib/session';
import { useStableCallback } from './hooks';
import { codeTone, fmtTime, relTime } from './format';
import { useDeleteWebhook, useEnableWebhook, useWebhookDeliveries, useWebhookSubscriptions } from './queries';
import type { WebhookDeliveryRow, WebhookSubscriptionRow } from './types';

const DELIVERY_STATUS: Record<WebhookDeliveryRow['status'], ['ok' | 'warn' | 'danger', string]> = {
  delivered: ['ok', 'נמסר'],
  pending: ['warn', 'בהמתנה'],
  failed: ['danger', 'נכשל'],
};

function TableSkel() {
  return (
    <div className="panel-body" aria-busy="true">
      <div className="skel" style={{ height: 18, marginBottom: 10 }} />
      <div className="skel" style={{ height: 18, marginBottom: 10 }} />
      <div className="skel" style={{ height: 18 }} />
    </div>
  );
}

function InlineError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <div className="panel-body">
      <div className="callout danger">
        <Icon name="alert" />
        <div className="grow">
          {errorMessage(error)}{' '}
          <button className="btn sm" onClick={onRetry}>
            <Icon name="refresh" />
            נסו שוב
          </button>
        </div>
      </div>
    </div>
  );
}

export { InlineError, TableSkel };

/** "Webhooks" panel: subscriptions created by external systems (POST /api/v1/webhooks) + recent deliveries. */
export function WebhooksPanel() {
  const can = useCan();
  const manage = can('api_keys.manage');
  const subs = useWebhookSubscriptions();
  const [filter, setFilter] = useState<string>();
  const deliveries = useWebhookDeliveries(filter, 30);
  const enable = useEnableWebhook();
  const [toDelete, setToDelete] = useState<WebhookSubscriptionRow | null>(null);
  const del = useDeleteWebhook();
  const closeDelete = useStableCallback(() => setToDelete(null));

  return (
    <>
      <div className="panel flush">
        <div className="panel-head">
          <h2>Webhooks</h2>
          <span className="hint">המערכות המחוברות נרשמות לעדכוני סטטוס דרך ה-API. כל עדכון נשלח חתום, עם ניסיונות חוזרים עד 24 שעות.</span>
        </div>
        {subs.isLoading ? (
          <TableSkel />
        ) : subs.isError ? (
          <InlineError error={subs.error} onRetry={() => void subs.refetch()} />
        ) : !subs.data?.length ? (
          <div className="empty">
            <div className="art">
              <Icon name="link" />
            </div>
            <h2>אין עדיין Webhooks</h2>
            <p>
              מערכת עם הרשאת <code className="ltr">webhooks:manage</code> נרשמת בבקשת <code className="ltr">POST /api/v1/webhooks</code>. אפשר גם
              לשלוח <code className="ltr">callbackUrl</code> עם כל הודעה.
            </p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>כתובת</th>
                  <th>מפתח</th>
                  <th className="hide-sm">אירועים</th>
                  <th>24 שעות</th>
                  <th>מצב</th>
                  <th>
                    <span className="sr-only">פעולות</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {subs.data.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <div className="cell-title ltr" style={{ wordBreak: 'break-all' }}>
                        {s.url}
                      </div>
                      <div className="cell-sub">נוצר {relTime(s.createdAt)}</div>
                    </td>
                    <td>
                      <div>{s.keyName}</div>
                      <div className="cell-sub">
                        <code className="ltr">{s.keyPrefix}••••</code>
                      </div>
                    </td>
                    <td className="hide-sm">
                      {s.events.map((e) => (
                        <span key={e} className="tag ltr" style={{ marginInlineEnd: 4 }}>
                          {e.replace(/^message\./, '')}
                        </span>
                      ))}
                    </td>
                    <td className="small">
                      <span className="num">{s.deliveries24h.delivered}</span> נמסרו
                      {s.deliveries24h.pending ? <div className="muted">{s.deliveries24h.pending} בהמתנה</div> : null}
                      {s.deliveries24h.failed ? <div style={{ color: 'var(--danger-ink)' }}>{s.deliveries24h.failed} נכשלו</div> : null}
                    </td>
                    <td>
                      {s.enabled ? (
                        <Pill tone="ok">פעיל</Pill>
                      ) : (
                        <>
                          <Pill tone="danger">הושבת</Pill>
                          <div className="cell-sub">
                            {s.failureCount} כישלונות רצופים{s.disabledAt ? ` · ${relTime(s.disabledAt)}` : ''}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="actions">
                      <button className="btn sm" onClick={() => setFilter(filter === s.id ? undefined : s.id)} aria-pressed={filter === s.id}>
                        משלוחים
                      </button>{' '}
                      {manage && !s.enabled ? (
                        <>
                          <button
                            className="btn sm"
                            disabled={enable.isPending}
                            onClick={() =>
                              enable.mutate(s.id, {
                                onSuccess: () => toast('ה-Webhook הופעל מחדש'),
                                onError: (e) => toast(errorMessage(e), 'error'),
                              })
                            }
                          >
                            הפעלה מחדש
                          </button>{' '}
                        </>
                      ) : null}
                      {manage ? (
                        <button className="btn sm danger" onClick={() => setToDelete(s)}>
                          מחיקה
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="panel flush">
        <div className="panel-head">
          <h2>משלוחי Webhook אחרונים</h2>
          {filter ? (
            <div className="actions">
              <button className="btn sm ghost" onClick={() => setFilter(undefined)}>
                <Icon name="x" />
                כל הכתובות
              </button>
            </div>
          ) : null}
        </div>
        {deliveries.isLoading ? (
          <TableSkel />
        ) : deliveries.isError ? (
          <InlineError error={deliveries.error} onRetry={() => void deliveries.refetch()} />
        ) : !deliveries.data?.length ? (
          <div className="panel-body small muted">אין משלוחים עדיין.</div>
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>שעה</th>
                  <th>אירוע</th>
                  <th>מצב</th>
                  <th className="hide-sm">פירוט</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.data.map((d) => {
                  const [tone, label] = DELIVERY_STATUS[d.status];
                  return (
                    <tr key={d.id}>
                      <td className="num">{fmtTime(d.createdAt)}</td>
                      <td>
                        <code className="ltr">{d.eventType}</code>
                        {d.messageId ? <div className="cell-sub ltr">{d.messageId}</div> : null}
                      </td>
                      <td>
                        <Pill tone={tone}>{label}</Pill>
                        {d.lastStatusCode ? (
                          <>
                            {' '}
                            <span className={`pill ${codeTone(d.lastStatusCode)}`}>{d.lastStatusCode}</span>
                          </>
                        ) : null}
                      </td>
                      <td className="hide-sm small muted">
                        <div className="ltr" style={{ wordBreak: 'break-all' }}>
                          {d.url}
                        </div>
                        {d.attempts > 1 ? <div>{d.attempts} ניסיונות</div> : null}
                        {d.lastError ? <div>{d.lastError}</div> : null}
                        {d.status === 'pending' && d.nextAttemptAt ? <div>ניסיון הבא {fmtTime(d.nextAttemptAt)}</div> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={Boolean(toDelete)}
        title="מחיקת Webhook"
        onClose={closeDelete}
        footer={
          <>
            <button
              className="btn danger"
              disabled={del.isPending}
              onClick={() =>
                toDelete &&
                del.mutate(toDelete.id, {
                  onSuccess: () => {
                    toast('ה-Webhook נמחק');
                    if (filter === toDelete.id) setFilter(undefined);
                    setToDelete(null);
                  },
                  onError: (e) => toast(errorMessage(e), 'error'),
                })
              }
            >
              {del.isPending ? 'מוחק…' : 'מחיקה'}
            </button>
            <button className="btn" onClick={() => setToDelete(null)}>
              ביטול
            </button>
          </>
        }
      >
        <p className="small">
          הכתובת <code className="ltr">{toDelete?.url}</code> לא תקבל עוד עדכוני סטטוס. המערכת של {toDelete?.keyName} תצטרך להירשם מחדש.
        </p>
      </Modal>
    </>
  );
}
